import React, { useCallback, useEffect, useState } from 'react';
import { adminFetch, useAdminToken } from './adminAuth';
import AdminLogin from './AdminLogin';
import AdminNav from './AdminNav';
import useHead from '../useHead';

function AdminTable({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [dormantOnly, setDormantOnly] = useState(false);

  // Dormant filtering happens server-side because the definition — no email, nothing
  // paid, no progress, no favorites, unseen for 30 days — lives in the User scope.
  const loadUsers = useCallback(() => {
    const path = `/api/v1/admin/users${dormantOnly ? '?filter=dormant' : ''}`;
    return adminFetch(token, path)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load users');
        return res.json();
      })
      .then(setData)
      .catch(() => setError('Failed to load users'));
  }, [token, dormantOnly]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function handleSetPremiumUntil(user, dateValue) {
    setBusyId(user.id);
    try {
      const res = await adminFetch(token, `/api/v1/admin/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_premium_until: dateValue ? `${dateValue}T23:59:59` : null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update premium access');
      }
      await loadUsers();
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(user) {
    const label = user.email || `anonymous account ${user.id.slice(0, 8)}`;

    if (!window.confirm(`Permanently delete ${label}?\n\nThis erases their progress, favorites and subscription records. It cannot be undone.`)) {
      return;
    }

    setBusyId(user.id);
    try {
      const res = await adminFetch(token, `/api/v1/admin/users/${user.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete user');
      }
      await loadUsers();
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p className="admin-error">{error}</p>;
  if (!data) return <p>Loading…</p>;

  const { users, dormant_count: dormantCount, total_count: totalCount } = data;

  return (
    <div className="admin-page">
      <AdminNav />
      <h1>Users ({totalCount})</h1>
      <p className="admin-muted">
        {dormantCount} dormant — anonymous, nothing paid, no progress or favorites, unseen for 30 days.
      </p>
      <label className="admin-checkbox">
        <input
          type="checkbox"
          checked={dormantOnly}
          onChange={(e) => setDormantOnly(e.target.checked)}
        />
        Show dormant only
      </label>
      <table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Joined</th>
            <th>Last seen</th>
            <th>Subscription</th>
            <th>Admin premium until</th>
            <th>Danger zone</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                {user.email || (
                  // Device accounts have no email, so show a short id to tell them apart.
                  <span className="admin-muted" title={user.id}>
                    anon · {user.id.slice(0, 8)}
                  </span>
                )}
              </td>
              <td>{new Date(user.created_at).toLocaleDateString()}</td>
              <td>
                {user.last_seen_at ? (
                  new Date(user.last_seen_at).toLocaleDateString()
                ) : (
                  <span className="admin-muted">never</span>
                )}
                {user.dormant && <span className="admin-badge admin-badge--inactive">dormant</span>}
              </td>
              <td>
                {user.subscriptions.length === 0 && <span className="admin-muted">none</span>}
                {user.subscriptions.map((sub) => (
                  <span
                    key={sub.entitlement_id}
                    className={`admin-badge ${sub.active ? 'admin-badge--active' : 'admin-badge--inactive'}`}
                  >
                    {sub.entitlement_id}: {sub.status}
                    {sub.expires_at && ` (expires ${new Date(sub.expires_at).toLocaleDateString()})`}
                  </span>
                ))}
              </td>
              <td>
                <PremiumUntilEditor
                  user={user}
                  busy={busyId === user.id}
                  onSave={(dateValue) => handleSetPremiumUntil(user, dateValue)}
                />
              </td>
              <td>
                <DeleteUserButton
                  user={user}
                  busy={busyId === user.id}
                  onDelete={() => handleDelete(user)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Paying accounts can't be deleted — removing one leaves the customer billed by Apple
// with no access. The server enforces this too; this just explains it up front.
function DeleteUserButton({ user, busy, onDelete }) {
  const activeSub = user.subscriptions.find((sub) => sub.active);

  if (activeSub) {
    return (
      <span
        className="admin-muted"
        title={`Active "${activeSub.entitlement_id}" subscription. Deleting would not stop their App Store billing — wait for it to lapse, or have them cancel first.`}
      >
        Paid — can't delete
      </span>
    );
  }

  return (
    <button className="admin-action-btn admin-action-btn--danger" disabled={busy} onClick={onDelete}>
      {busy ? 'Working…' : 'Delete'}
    </button>
  );
}

function PremiumUntilEditor({ user, busy, onSave }) {
  const initialValue = user.admin_premium_until ? user.admin_premium_until.slice(0, 10) : '';
  const [value, setValue] = useState(initialValue);

  return (
    <div className="admin-actions">
      <input
        type="date"
        className="admin-date-input"
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
      />
      <button className="admin-action-btn" disabled={busy} onClick={() => onSave(value)}>
        Save
      </button>
      {initialValue && (
        <button
          className="admin-action-btn admin-action-btn--danger"
          disabled={busy}
          onClick={() => {
            setValue('');
            onSave(null);
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useAdminToken();

  useHead({ title: 'Admin — ConvoLines', description: '', path: '/admin', noindex: true });

  if (!token) return <AdminLogin onAuthed={setToken} />;
  return <AdminTable token={token} />;
}
